// Replicate API Service
// Virtual Try-On using Replicate ML Models

import axios from 'axios';

const REPLICATE_API_KEY = process.env.REACT_APP_REPLICATE_API_KEY || '';
const REPLICATE_API_BASE_URL = 'https://api.replicate.com/v1';

class ReplicateService {
  /**
   * Check if Replicate API is configured
   * @returns {boolean}
   */
  isConfigured() {
    return !!REPLICATE_API_KEY && REPLICATE_API_KEY.length > 0;
  }

  /**
   * Create a prediction (run a model)
   * @param {string} modelVersion - Model version identifier
   * @param {Object} input - Input parameters for the model
   * @returns {Promise<string>} Prediction ID
   */
  async createPrediction(modelVersion, input) {
    try {
      if (!this.isConfigured()) {
        throw new Error('Replicate API key not configured. Please set REACT_APP_REPLICATE_API_KEY in your .env file.');
      }

      const response = await axios.post(
        `${REPLICATE_API_BASE_URL}/predictions`,
        {
          version: modelVersion,
          input: input
        },
        {
          headers: {
            'Authorization': `Token ${REPLICATE_API_KEY}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      return response.data.id;
    } catch (error) {
      console.error('Error creating prediction:', error);
      if (error.response) {
        throw new Error(`Replicate API error: ${error.response.data?.detail || error.response.statusText}`);
      }
      throw error;
    }
  }

  /**
   * Get prediction status and result
   * @param {string} predictionId - Prediction ID
   * @returns {Promise<Object>} Prediction status and output
   */
  async getPrediction(predictionId) {
    try {
      const response = await axios.get(
        `${REPLICATE_API_BASE_URL}/predictions/${predictionId}`,
        {
          headers: {
            'Authorization': `Token ${REPLICATE_API_KEY}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error getting prediction:', error);
      if (error.response) {
        throw new Error(`Replicate API error: ${error.response.data?.detail || error.response.statusText}`);
      }
      throw error;
    }
  }

  /**
   * Poll for prediction result
   * @param {string} predictionId - Prediction ID
   * @param {number} maxAttempts - Maximum polling attempts
   * @param {number} interval - Polling interval in ms
   * @returns {Promise<string>} Result image URL
   */
  async pollPrediction(predictionId, maxAttempts = 60, interval = 2000) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const prediction = await this.getPrediction(predictionId);
        
        console.log(`Poll attempt ${attempt + 1}/${maxAttempts} - Status: ${prediction.status}`);

        if (prediction.status === 'succeeded') {
          // Output can be a string (URL) or array of URLs
          const output = prediction.output;
          if (Array.isArray(output) && output.length > 0) {
            return output[0];
          } else if (typeof output === 'string') {
            return output;
          } else {
            throw new Error('Unexpected output format from Replicate API');
          }
        } else if (prediction.status === 'failed' || prediction.status === 'canceled') {
          const error = prediction.error || 'Prediction failed';
          throw new Error(`Replicate prediction failed: ${error}`);
        } else if (prediction.status === 'starting' || prediction.status === 'processing') {
          // Still processing, wait and retry
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        } else {
          // Unknown status, wait and retry
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        }
      } catch (error) {
        if (error.message.includes('failed') || error.message.includes('canceled')) {
          throw error;
        }
        
        // For other errors, retry if not last attempt
        if (attempt < maxAttempts - 1) {
          console.warn(`Polling attempt ${attempt + 1} failed:`, error.message);
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        }
        throw new Error(`Failed to get prediction result after ${maxAttempts} attempts: ${error.message}`);
      }
    }

    throw new Error(`Prediction did not complete within timeout period (${(maxAttempts * interval) / 1000} seconds)`);
  }

  /**
   * Upload image to Replicate and get URL
   * @param {File|string} imageFile - Image file or data URL
   * @returns {Promise<string>} Image URL
   */
  async uploadImage(imageFile) {
    try {
      // If it's already a URL, return it
      if (typeof imageFile === 'string' && (imageFile.startsWith('http://') || imageFile.startsWith('https://'))) {
        return imageFile;
      }

      // Convert File or blob URL to base64 data URL
      let dataUrl;
      if (imageFile instanceof File) {
        dataUrl = await this.fileToDataURL(imageFile);
      } else if (typeof imageFile === 'string' && imageFile.startsWith('blob:')) {
        const response = await fetch(imageFile);
        const blob = await response.blob();
        dataUrl = await this.blobToDataURL(blob);
      } else if (typeof imageFile === 'string' && imageFile.startsWith('data:')) {
        dataUrl = imageFile;
      } else {
        throw new Error('Invalid image format');
      }

      // Replicate accepts data URLs directly, but we can also upload to a temporary URL
      // For now, we'll use data URLs (base64) which Replicate supports
      return dataUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error(`Failed to upload image: ${error.message}`);
    }
  }

  /**
   * Convert File to data URL
   * @param {File} file - File object
   * @returns {Promise<string>} Data URL
   */
  fileToDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Convert Blob to data URL
   * @param {Blob} blob - Blob object
   * @returns {Promise<string>} Data URL
   */
  blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Apply hairstyle using Replicate model
   * @param {File|string} userPhoto - User's photo
   * @param {string} styleId - Hairstyle identifier
   * @returns {Promise<string>} Result image URL
   */
  async applyHairstyle(userPhoto, styleId) {
    try {
      // Upload user image
      const imageUrl = await this.uploadImage(userPhoto);

      // Using face-swap or virtual try-on models
      // Note: You may need to adjust model versions based on what's available
      // Popular models: 
      // - "yan-ops/face_swap:latest" for face swapping
      // - "logerzhu/virtual-try-on:latest" for virtual try-on
      // - "cuuupid/idm-vton:latest" for virtual try-on
      
      // For hairstyle, we'll use a face swap or virtual try-on model
      // If you have a specific hairstyle reference image, you can use it
      const modelVersion = 'yan-ops/face_swap:latest'; // Face swap model
      
      const input = {
        source_image: imageUrl,
        // If you have a target hairstyle image, add it here
        // target_image: styleReferenceImageUrl,
      };

      console.log('Creating Replicate prediction for hairstyle...');
      const predictionId = await this.createPrediction(modelVersion, input);
      
      console.log('Polling for result...');
      return await this.pollPrediction(predictionId);
    } catch (error) {
      console.error('Error applying hairstyle:', error);
      // If face swap fails, try virtual try-on model
      if (error.message.includes('face') || error.message.includes('swap')) {
        console.log('Trying alternative virtual try-on model...');
        try {
          const imageUrl = await this.uploadImage(userPhoto);
          const modelVersion = 'logerzhu/virtual-try-on:latest';
          const input = { image: imageUrl };
          const predictionId = await this.createPrediction(modelVersion, input);
          return await this.pollPrediction(predictionId);
        } catch (fallbackError) {
          throw new Error(`Failed to apply hairstyle: ${fallbackError.message}`);
        }
      }
      throw error;
    }
  }

  /**
   * Apply makeup using Replicate model
   * @param {File|string} userPhoto - User's photo
   * @param {string} styleId - Makeup style identifier
   * @returns {Promise<string>} Result image URL
   */
  async applyMakeup(userPhoto, styleId) {
    try {
      // Upload user image
      const imageUrl = await this.uploadImage(userPhoto);

      // Use a virtual try-on or face enhancement model for makeup
      // Popular models:
      // - "logerzhu/virtual-try-on:latest" for virtual try-on
      // - "cuuupid/idm-vton:latest" for virtual try-on
      // - Face enhancement models for makeup effects
      
      const modelVersion = 'logerzhu/virtual-try-on:latest';
      
      const input = {
        image: imageUrl,
        // Add makeup-specific parameters if the model supports them
      };

      console.log('Creating Replicate prediction for makeup...');
      const predictionId = await this.createPrediction(modelVersion, input);
      
      console.log('Polling for result...');
      return await this.pollPrediction(predictionId);
    } catch (error) {
      console.error('Error applying makeup:', error);
      throw error;
    }
  }

  /**
   * Get available hairstyles (default list)
   * @returns {Array} List of hairstyles
   */
  getDefaultHairstyles() {
    return [
      { id: 'bob-cut', name: 'Bob Cut', thumbnailUrl: null },
      { id: 'pixie-cut', name: 'Pixie Cut', thumbnailUrl: null },
      { id: 'long-wavy', name: 'Long Wavy', thumbnailUrl: null },
      { id: 'curly', name: 'Curly', thumbnailUrl: null },
      { id: 'straight', name: 'Straight', thumbnailUrl: null },
      { id: 'ponytail', name: 'Ponytail', thumbnailUrl: null },
      { id: 'bun', name: 'Bun', thumbnailUrl: null },
      { id: 'braids', name: 'Braids', thumbnailUrl: null },
    ];
  }

  /**
   * Get available makeup styles (default list)
   * @returns {Array} List of makeup styles
   */
  getDefaultMakeupStyles() {
    return [
      { id: 'natural', name: 'Natural', thumbnailUrl: null },
      { id: 'glam', name: 'Glam', thumbnailUrl: null },
      { id: 'smoky', name: 'Smoky Eye', thumbnailUrl: null },
      { id: 'bold-lip', name: 'Bold Lip', thumbnailUrl: null },
      { id: 'soft', name: 'Soft', thumbnailUrl: null },
    ];
  }
}

export default new ReplicateService();
